import { CrudForm } from '../shared/CrudPage';
import { bannerService } from '@kss/api';
import type { Banner, BannerCreate, BannerFilter } from '@kss/api';
import { persistImage } from '../../utils/persistImage';

type F = {
  title: string;
  subtitle: string;
  imageUrl: string;
  ctaLabel: string;
  ctaUrl: string;
  sortOrder: number;
  isActive: boolean;
};
const empty: F = {
  title: '',
  subtitle: '',
  imageUrl: '',
  ctaLabel: '',
  ctaUrl: '',
  sortOrder: 0,
  isActive: true,
};

export default function NewBanner() {
  return (
    <CrudForm<Banner, BannerCreate, BannerFilter, F>
      title="banner"
      service={bannerService}
      idKey="bannerId"
      basePath="/panel/banners"
      emptyForm={empty}
      fields={[
        /* Title is OPTIONAL — the client only wants to upload a
           banner image and doesn't want to type a headline. The
           storefront overlay (see `bannerSlide()` in
           `apps/web/src/shop/ShopList.tsx`) now skips the title
           block entirely when it's blank. Bug ref: AI Agent
           2026-06-11 — "remove the mandatory validation for the
           Title field on banner". */
        { key: 'title', label: 'Title (optional)', placeholder: 'e.g. Mega electronics sale — leave blank for image-only' },
        { key: 'subtitle', label: 'Subtitle', placeholder: 'Up to 60% off', fullWidth: true },
        /* Accepts both IMAGES and VIDEOS — paste a hosted URL or
           upload a file. Video URLs (.mp4 / .webm / .ogg / .mov / .m4v)
           are auto-detected by the storefront carousel and play in
           the slide, advancing to the next banner when the video
           ends. Image slides still rotate on the standard 4-second
           interval. Mixed playlists (image + video together) are
           supported. */
        {
          key: 'imageUrl',
          label: 'Image or video',
          type: 'image',
          placeholder: 'Paste an image URL or a video URL (.mp4 / .webm / .ogg / .mov).',
          fullWidth: true,
        },
        { key: 'ctaLabel', label: 'CTA label', placeholder: 'Shop deals' },
        { key: 'ctaUrl', label: 'CTA URL', placeholder: '/shop?c=electronics' },
        { key: 'sortOrder', label: 'Sort order', type: 'number' },
        { key: 'isActive', label: 'Active', type: 'status' },
      ]}
      fromEntity={(r) => ({
        title: r.title ?? '',
        subtitle: r.subtitle ?? '',
        imageUrl: r.imageUrl ?? '',
        ctaLabel: r.ctaLabel ?? '',
        ctaUrl: r.ctaUrl ?? '',
        sortOrder: Number(r.sortOrder ?? 0),
        isActive: !!r.isActive,
      })}
      toEntity={async (f, id) => ({
        bannerId: id,
        /* Backend column is NOT NULL on `Title`, so substitute a
           single space when the operator left it blank. The
           storefront's `bannerSlide()` treats whitespace-only titles
           as "no overlay text" and skips the title element entirely,
           so the rendered banner is still image-only as intended. */
        title: f.title.trim() || ' ',
        subtitle: f.subtitle || undefined,
        // Upload base64 data URLs to /api/Image/upload first, then
        // send the persisted URL with the save. Hosted URLs pass
        // through unchanged. See utils/persistImage.ts. The `banner`
        // context routes to /uploads/{tenantId}/banner/...
        imageUrl: await persistImage(f.imageUrl, 'banner'),
        ctaLabel: f.ctaLabel || undefined,
        ctaUrl: f.ctaUrl || undefined,
        sortOrder: f.sortOrder || 0,
        isActive: f.isActive,
      })}
    />
  );
}
